import { useMemo, useState } from 'react';
import {
  AdCampaignsDataContextProvider,
  useAdCampaignsDataContext,
} from 'src/contexts/AdCampaignsDataContext/AdCampaignsDataContext';
import { CatalogDataContextProvider } from 'src/contexts/CatalogDataContext';
import { Content, useContent } from 'src/contexts/ContentContext';
import { DialogProvider } from 'src/contexts/DialogContext/DialogContext';
import { FilterQueryContextProvider } from 'src/contexts/FilterQueryContext';
import { PosSpinner } from 'src/core/POS/PosSpinner';
import { Stack, Switch } from 'src/core/ui';
import { LayoutContent } from 'src/navigations/LayoutContent';
import { CampaignsTableV2 } from 'src/tables/SponsoredListings';
import { ContentId } from 'src/utils/constants/contentId';
import { getLastDateRange } from 'src/utils/dateTimeUtils';
import {
  DefaultListingQuery,
  EmptyListingQuery,
} from 'src/utils/eventQueryUtils';
import { transformData } from 'src/utils/eventWithDataUtils';
import { ListingCatalogQueryKey } from 'src/utils/inventoryUtils';
import { SomethingWentWrong } from 'src/views';
import {
  ActionOutboxEntityType,
  AdCampaignState,
  DateTimeRange,
  ListingQuery,
  UserSetting,
} from 'src/WebApiController';

import { MainRoute } from '../MainRoute';
import * as styles from './SponsoredListings.css';

export function SponsoredListings() {
  return (
    <FilterQueryContextProvider<ListingQuery>
      initialQuery={
        {
          ...DefaultListingQuery,
          eventDates: {
            start: getLastDateRange(
              { days: 30 },
              new Date()
            ).start.toISOString(),
            end: null,
          } as DateTimeRange,
        } as ListingQuery
      }
      emptyQuery={EmptyListingQuery}
      viewModeSettingId={UserSetting.InventoryPageViewMode}
      saveQueryInUrl
    >
      <CatalogDataContextProvider<ListingQuery>
        entityType={ActionOutboxEntityType.Listing}
        queryKey={ListingCatalogQueryKey}
        transformEventData={transformData}
      >
        <AdCampaignsDataContextProvider loadSalesDetails>
          <SponsoredListingsContent />
        </AdCampaignsDataContextProvider>
      </CatalogDataContextProvider>
    </FilterQueryContextProvider>
  );
}

function SponsoredListingsContent() {
  const sponsoredListingsTitle = useContent(ContentId.SponsoredListings);

  const [isLoading, setIsLoading] = useState(() => false);

  const [hideActive, setHideActive] = useState(false);
  const [hidePrevious, setHidePrevious] = useState(false);
  const [hideOlder, setHideOlder] = useState(true);

  const {
    allCampaignData,
    adCampaignsDataQuery: adCampaignsDataQuery,
    errorInfo,
    refreshData,
  } = useAdCampaignsDataContext();

  const allCampaigns = useMemo(() => {
    if (!allCampaignData) {
      return null;
    }
    return Object.values(allCampaignData).flat();
  }, [allCampaignData]);

  const { activeCampaigns, inactiveCampaigns } = useMemo(() => {
    if (allCampaigns == null) {
      return { activeCampaigns: null, inactiveCampaigns: null };
    }
    const activeCampaigns = allCampaigns.filter(
      (c) => c.campaignState == AdCampaignState.Active && !c.hasEnded
    );

    const inactiveCampaigns = allCampaigns.filter(
      (c) => c.campaignState == AdCampaignState.Paused || c.hasEnded
    );
    return { activeCampaigns, inactiveCampaigns };
  }, [allCampaigns]);

  return (
    <DialogProvider>
      <LayoutContent
        mainRoute={MainRoute.SponsoredListings}
        routeTitle={sponsoredListingsTitle}
      >
        <Stack
          direction="column"
          width="full"
          height="full"
          className={styles.tableSection}
        >
          {adCampaignsDataQuery.isLoading && <PosSpinner />}
          {errorInfo && (
            <SomethingWentWrong
              header={errorInfo.errorHeader}
              message={
                errorInfo.errorMessage ?? (
                  <Content id={ContentId.FailToLoadListContent} />
                )
              }
            />
          )}
          {!adCampaignsDataQuery.isLoading && !errorInfo && (
            <>
              <Stack
                gap="l"
                direction="column"
                width="full"
                style={{
                  height: activeCampaigns?.length ? '100%' : 'fit-content',
                }}
              >
                <Stack gap="l" justifyContent="spaceBetween">
                  <span>
                    <Content id={ContentId.ActiveCampaigns} /> (
                    {activeCampaigns?.length ?? 0})
                  </span>
                  <Stack gap="l" direction="row">
                    <Stack gap="s" direction="row" alignItems="center">
                      <Switch
                        checked={!hideActive}
                        onCheckedChange={() => setHideActive(!hideActive)}
                      />
                      <span>
                        <Content id={ContentId.ShowCurrent} />
                      </span>
                    </Stack>
                    <Stack gap="s" direction="row" alignItems="center">
                      <Switch
                        checked={!hidePrevious}
                        onCheckedChange={() => setHidePrevious(!hidePrevious)}
                      />
                      <span>
                        <Content id={ContentId.ShowPrevious} />
                      </span>
                    </Stack>
                    <Stack gap="s" direction="row" alignItems="center">
                      <Switch
                        checked={!hideOlder}
                        onCheckedChange={() => setHideOlder(!hideOlder)}
                      />
                      <span>
                        <Content id={ContentId.ShowOlder} />
                      </span>
                    </Stack>
                  </Stack>
                </Stack>
                <CampaignsTableV2
                  showEventColumns
                  disabled={isLoading}
                  isLoading={adCampaignsDataQuery.isLoading}
                  campaigns={activeCampaigns}
                  onStartWork={() => setIsLoading(true)}
                  onStopWork={(success) => {
                    setIsLoading(false);
                    if (success) refreshData();
                  }}
                  hideActive={hideActive}
                  hidePrevious={hidePrevious}
                  hideOlder={hideOlder}
                />
              </Stack>
              <Stack
                gap="l"
                direction="column"
                width="full"
                style={{
                  height: inactiveCampaigns?.length ? '100%' : 'fit-content',
                }}
              >
                <span>
                  <Content id={ContentId.InactiveCampaigns} /> (
                  {inactiveCampaigns?.length ?? 0})
                </span>
                <CampaignsTableV2
                  showEventColumns
                  disabled={isLoading}
                  isLoading={adCampaignsDataQuery.isLoading}
                  campaigns={inactiveCampaigns}
                  onStartWork={() => setIsLoading(true)}
                  onStopWork={(success) => {
                    setIsLoading(false);
                    if (success) refreshData();
                  }}
                  hideActive={hideActive}
                  hidePrevious={hidePrevious}
                  hideOlder={hideOlder}
                />
              </Stack>
            </>
          )}
        </Stack>
      </LayoutContent>
    </DialogProvider>
  );
}
